/**
 * Phase 2 — entity-aware strategy funding helpers (Aged Care manual control slice 1).
 * Spec §5.6, §9.1.3.
 */
import { ASSET_TYPES } from '../constants/index.ts';
import {
    calculateSharePartialDisposalTax,
    parseCgtOverride,
} from '../../../shared/src/calculation/entityDisposalTax.ts';
import {
    entityOwnershipValue,
    getEntityIdFromOwnership,
    getStructureTypeLabel,
    isEntityOwnership,
} from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import type { FactFind, FactFindAsset } from '../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

type DraftAsset = FactFindAsset;
type DraftAssetMap = NonNullable<DraftFactFind['assets']>;
type AssetTypeEntry = (typeof ASSET_TYPES)[number];

/** Spec §9.1.3 — shown when user allocates RAD from a structure. */
export const ENTITY_STRUCTURE_FUNDING_DISCLAIMER =
    'Confirm this action is allowed under the trust deed, company constitution, and for all owners or beneficiaries. This tool does not provide legal advice.';

/** Spec §9.1.2 — suggestion engine may use entity liquid assets / whole IP sale when true. */
export function isStructureFundingIncludedInSuggestions(factFind: DraftFactFind | null | undefined): boolean {
    return factFind?.structureFundingInSuggestions === 'include';
}

/** When exclude (default), entity-owned rows are not used in automatic funding suggestions. */
export function isAssetEligibleForSuggestionRadFunding(
    factFind: DraftFactFind | null | undefined,
    asset: DraftAsset | null | undefined,
): boolean {
    if (!asset) return false;
    if (!isStructureFundingIncludedInSuggestions(factFind) && isEntityOwnership(asset?.ownership)) {
        return false;
    }
    return true;
}

/** Entity liquid assets for RAD funding (bank, term deposits, shares / managed funds). */
export const ENTITY_RAD_FUNDING_ASSET_TYPE_IDS: Set<string> = new Set([
    'bankAccounts',
    'termDeposits',
    'shares',
]);

/**
 * @param key — e.g. `bankAccounts-cash1` or `bankAccounts-123`
 */
export function parseRadFundingAssetKey(key: unknown): { assetType: string; assetId: string } | null {
    if (!key || typeof key !== 'string') return null;
    const dash = key.indexOf('-');
    if (dash <= 0) return null;
    const assetType = key.slice(0, dash);
    const assetId = key.slice(dash + 1);
    if (!assetType || !assetId) return null;
    return { assetType, assetId };
}

/** Loose id match (string vs number asset ids in fact-find). */
export function assetIdsMatch(left: unknown, right: unknown): boolean {
    if (left == null || right == null) return false;
    return String(left) === String(right);
}

export function findAssetIndexById(assetList: DraftAsset[] | null | undefined, assetId: string | number): number {
    if (!Array.isArray(assetList)) return -1;
    return assetList.findIndex((a) => assetIdsMatch(a.id, assetId));
}

export function findAssetInFactFind(
    factFind: DraftFactFind | null | undefined,
    assetType: string,
    assetId: string | number,
): { asset: DraftAsset; index: number } | null {
    const list = factFind?.assets?.[assetType];
    const index = findAssetIndexById(list, assetId);
    if (index < 0) return null;
    return { asset: list![index]!, index };
}

function buildRadFundingAssetRow(type: AssetTypeEntry, asset: DraftAsset) {
    return {
        ...asset,
        typeId: type.id,
        typeName: type.name,
        assetId: `${type.id}-${asset.id}`,
        hasCgt: type.hasCgt,
    };
}

type RadFundingAssetRow = ReturnType<typeof buildRadFundingAssetRow>;

interface EntityAssetGroup {
    entityId: string;
    entityName: string;
    structureType?: string;
    structureTypeLabel: string;
    assets: RadFundingAssetRow[];
}

type EntryFeeAssetRow = RadFundingAssetRow & { isInvestmentProperty?: boolean };

interface EntityEntryFeeAssetGroup {
    entityId: string;
    entityName: string;
    structureType?: string;
    structureTypeLabel: string;
    assets: EntryFeeAssetRow[];
}

/**
 * Personal / joint liquid assets for RAD sliders (excludes entity-owned).
 */
export function getPersonalLiquidAssetsForRad(factFind: DraftFactFind): RadFundingAssetRow[] {
    const assets: RadFundingAssetRow[] = [];
    ASSET_TYPES.forEach((type) => {
        if (!type.isLiquid || !factFind.assets?.[type.id]) return;
        factFind.assets[type.id]!.forEach((asset) => {
            if (isEntityOwnership(asset.ownership)) return;
            assets.push(buildRadFundingAssetRow(type, asset));
        });
    });
    return assets;
}

/**
 * Entity liquid assets grouped by structure (bank, term deposits, shares).
 */
export function getEntityLiquidGroupsForRad(factFind: DraftFactFind): EntityAssetGroup[] {
    const structures = factFind.structures || [];
    if (!structures.length) return [];

    const byEntity = new Map<string, EntityAssetGroup>();

    structures.forEach((structure) => {
        byEntity.set(structure.id ?? '', {
            entityId: structure.id ?? '',
            entityName: structure.name?.trim() || 'Trust or company',
            structureType: structure.type,
            structureTypeLabel: getStructureTypeLabel(structure.type || ''),
            assets: [],
        });
    });

    ASSET_TYPES.forEach((type) => {
        if (!ENTITY_RAD_FUNDING_ASSET_TYPE_IDS.has(type.id)) return;
        if (!factFind.assets?.[type.id]) return;
        factFind.assets[type.id]!.forEach((asset) => {
            if (!isEntityOwnership(asset.ownership)) return;
            const entityId = getEntityIdFromOwnership(asset.ownership);
            if (!entityId) return;
            const group = byEntity.get(entityId);
            if (!group) return;
            group.assets.push(buildRadFundingAssetRow(type, asset));
        });
    });

    return [...byEntity.values()].filter((g) => g.assets.length > 0);
}

/**
 * Entity liquid + ABP + investment property grouped for RV entry-fee sliders (manual control).
 */
export function getEntityEntryFeeAssetGroups(factFind: DraftFactFind): EntityEntryFeeAssetGroup[] {
    const structures = factFind.structures || [];
    if (!structures.length) return [];

    const byEntity = new Map<string, EntityEntryFeeAssetGroup>();

    structures.forEach((structure) => {
        byEntity.set(structure.id ?? '', {
            entityId: structure.id ?? '',
            entityName: structure.name?.trim() || 'Trust or company',
            structureType: structure.type,
            structureTypeLabel: getStructureTypeLabel(structure.type || ''),
            assets: [],
        });
    });

    ASSET_TYPES.forEach((type) => {
        if (!type.isLiquid || !factFind.assets?.[type.id]) return;
        factFind.assets[type.id]!.forEach((asset) => {
            if (!isEntityOwnership(asset.ownership)) return;
            const entityId = getEntityIdFromOwnership(asset.ownership);
            if (!entityId) return;
            const group = byEntity.get(entityId);
            if (!group) return;
            group.assets.push(buildRadFundingAssetRow(type, asset));
        });
    });

    (factFind.assets?.investmentProperty || []).forEach((property) => {
        if (!isEntityOwnership(property.ownership)) return;
        const entityId = getEntityIdFromOwnership(property.ownership);
        if (!entityId) return;
        const group = byEntity.get(entityId);
        if (!group) return;
        group.assets.push({
            ...property,
            typeId: 'investmentProperty',
            typeName: 'Investment property',
            assetId: `investmentProperty-${property.id}`,
            hasCgt: true,
            isInvestmentProperty: true,
        });
    });

    return [...byEntity.values()].filter((g) => g.assets.length > 0);
}

/** Total entity RAD allocation across all structure groups. */
export function sumEntityRadFunding(
    radFunding: Record<string, number> = {},
    entityGroups: EntityAssetGroup[] = [],
): number {
    let total = 0;
    entityGroups.forEach((group) => {
        group.assets.forEach((asset) => {
            total += radFunding[asset.assetId] || 0;
        });
    });
    return total;
}

/**
 * Option A (net proceeds pool): gross liquidation vs net RAD contribution for one funding key.
 * Shares with cost base: net = gross − effective CGT; other assets: net = gross.
 */
export function resolveRadFundingEntry(
    factFind: DraftFactFind,
    assetKey: string,
    grossAmount: number | null | undefined,
    cgtOverrides: Record<string, unknown> = {},
): { gross: number; cgt: number; net: number } {
    const gross = Math.max(0, grossAmount || 0);
    if (gross <= 0) {
        return { gross: 0, cgt: 0, net: 0 };
    }

    const parsed = parseRadFundingAssetKey(assetKey);
    if (!parsed || parsed.assetType !== 'shares') {
        return { gross, cgt: 0, net: gross };
    }

    const located = findAssetInFactFind(factFind, parsed.assetType, parsed.assetId);
    if (!located?.asset) {
        return { gross, cgt: 0, net: gross };
    }
    // NB: a missing cost base no longer means $0 CGT — calculateSharePartialDisposalTax applies the
    // shared 25%-of-value assumed cost base (resolveCostBase), disclosed in-app and in the SOA.

    // DraftFactFind is structurally compatible with FactFind/FactFindAsset (see
    // types/formDraft.ts); the shared calc-engine fns defensively parseFloat/coerce internally
    // regardless (see factFindTypes.ts header), so this cast is safe.
    const taxResult = calculateSharePartialDisposalTax(
        factFind as unknown as FactFind,
        located.asset as unknown as FactFindAsset,
        gross,
        { trustDisposal: 'distribute' },
    );
    const override = parseCgtOverride(cgtOverrides[assetKey]);
    const cgt = override != null ? override : taxResult.estimatedCGT ?? 0;
    return { gross, cgt, net: Math.max(0, gross - cgt) };
}

/**
 * @param radFunding — manual keys `type-id` → gross liquidation
 */
export function computeRadFundingTotals(
    factFind: DraftFactFind,
    radFunding: Record<string, number> = {},
    cgtOverrides: Record<string, unknown> = {},
): {
    grossTotal: number;
    netTotal: number;
    shareCgtTotal: number;
    byKey: Record<string, { gross: number; cgt: number; net: number }>;
} {
    let grossTotal = 0;
    let netTotal = 0;
    let shareCgtTotal = 0;
    const byKey: Record<string, { gross: number; cgt: number; net: number }> = {};

    Object.entries(radFunding || {}).forEach(([key, amount]) => {
        const entry = resolveRadFundingEntry(factFind, key, amount, cgtOverrides);
        byKey[key] = entry;
        grossTotal += entry.gross;
        netTotal += entry.net;
        shareCgtTotal += entry.cgt;
    });

    return { grossTotal, netTotal, shareCgtTotal, byKey };
}

/**
 * RAD funding sources that are PAID ACROSS rather than sold — no disposal, so no sale date to nominate
 * and no capital gain to time. Everything else is a sale.
 */
const CASH_LIKE_RAD_FUNDING_TYPES: ReadonlySet<string> = new Set(['bankAccounts', 'termDeposits']);

/**
 * Is anything actually being SOLD to fund the deposit?
 *
 * Gates the "sell the assets before care starts?" control. Without this it appeared the moment a care
 * date was entered — asking when the assets would be sold of clients who had allocated nothing to a
 * lump sum at all and were simply paying the daily fee. That reads as the tool having decided
 * something will be sold, which is not a small thing to imply to someone working out how to fund
 * their own or a parent's care.
 *
 * Cash is not sold: money already in a bank account is paid across on the day, so the timing cannot
 * change anything. A share parcel, an investment property or the family home is a disposal — the
 * proceeds sit in the bank until the deposit is paid (deemed, and counted in the assets test) and the
 * CGT falls on the day of sale. A reverse mortgage is drawn, not sold, so it does not count either.
 */
export function radFundingInvolvesAssetSale(
    radFunding: Record<string, number> = {},
    saleAllocations: { home?: unknown; investmentProperty?: unknown } = {},
): boolean {
    const sellsLiquidAsset = Object.entries(radFunding || {}).some(([key, amount]) => {
        if (!((parseFloat(String(amount)) || 0) > 0)) return false;
        const assetType = parseRadFundingAssetKey(key)?.assetType;
        return !!assetType && !CASH_LIKE_RAD_FUNDING_TYPES.has(assetType);
    });
    const allocated = (raw: unknown) => (parseFloat(String(raw)) || 0) > 0;
    return sellsLiquidAsset || allocated(saleAllocations.home) || allocated(saleAllocations.investmentProperty);
}

/** First personal/joint bank account (excludes entity-owned rows). */
export function findPersonalBankAccount(assets: DraftAssetMap | null | undefined): DraftAsset | null {
    const list = assets?.bankAccounts;
    if (!Array.isArray(list) || list.length === 0) return null;
    return (
        list.find((a) => !isEntityOwnership(a?.ownership)) ||
        list[0]!
    );
}

/**
 * Debit CGT cash impact from the pool matching asset ownership (entity bank or personal).
 * Used by SAH/RV manual strategy paths when liquidating shares or investment property.
 */
export function debitCgtFromOwnershipPool(
    assets: DraftAssetMap | null | undefined,
    cgtAmount: number | null | undefined,
    ownership: string | undefined,
): void {
    const cgt = Math.max(0, cgtAmount || 0);
    if (cgt <= 0 || !assets?.bankAccounts?.length) return;

    if (isEntityOwnership(ownership)) {
        const entityId = getEntityIdFromOwnership(ownership);
        const entityOwn = entityOwnershipValue(entityId ?? '');
        const entityBank =
            assets.bankAccounts.find((a) => a.ownership === entityOwn) || null;
        if (entityBank) {
            entityBank.value = Math.max(0, (entityBank.value || 0) - cgt);
            return;
        }
    }

    const personalBank = findPersonalBankAccount(assets);
    if (personalBank) {
        personalBank.value = Math.max(0, (personalBank.value || 0) - cgt);
    }
}

/** Debit a lump sum from personal bank first, then any bank if needed. */
export function debitFromPersonalBankFirst(assets: DraftAssetMap | null | undefined, amount: number | null | undefined): void {
    const debit = Math.max(0, amount || 0);
    if (debit <= 0 || !assets?.bankAccounts?.length) return;

    const personalBank = findPersonalBankAccount(assets);
    if (!personalBank) return;

    personalBank.value = Math.max(0, (personalBank.value || 0) - debit);
}
